import { useState } from "react";
import { Percent } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";

export const priceForMargin = (cost, m) => Math.round(cost * (1 + m / 100) * 100) / 100;

export function MoneyInput({ value, onValue, ...props }) {
  const [text, setText] = useState(null);
  const shown = text ?? (value ? String(Math.round(value * 100) / 100) : "");
  return (
    <Input type="number" step="0.01" value={shown} {...props}
      onFocus={() => setText(shown)} onBlur={() => setText(null)}
      onChange={(e) => { setText(e.target.value); onValue(+e.target.value || 0); }} />
  );
}

export function TargetMarginInput({ value, onChange, locked, invalid }) {
  return (
    <div className={`flex items-center gap-2 rounded-md border-2 px-3 h-10 ${invalid ? "border-rose-500 bg-rose-500/10" : "border-[#d32f2f]/60 bg-[#d32f2f]/5"}`} data-testid="target-margin-bar">
      <Percent className="h-4 w-4 text-[#d32f2f]" />
      <Label htmlFor="target-margin" className="text-xs font-semibold whitespace-nowrap">Target Profit Margin %</Label>
      <Input id="target-margin" type="number" min={0} step="0.5" disabled={locked} value={value} className="h-8 w-20 num font-semibold bg-background"
        onChange={(e) => onChange(e.target.value === "" ? "" : +e.target.value)} data-testid="target-margin-input" />
    </div>
  );
}

export function QuotePricingSheet({ rules, globals, onChange, canEdit, locked }) {
  const [open, setOpen] = useState(false);
  const set = (k) => (e) => onChange({ ...rules, [k]: e.target.value === "" ? "" : +e.target.value });
  const off = !canEdit || locked;
  const bad = (v) => v !== "" && !(+v >= 0 && +v < 95);
  return (
    <>
      <Button variant="outline" onClick={() => setOpen(true)} data-testid="open-quote-pricing-button"><Percent className="h-4 w-4 mr-2" />Quote pricing rules</Button>
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="right" className="w-full sm:max-w-md" data-testid="quote-pricing-sheet">
          <SheetHeader className="mb-5"><SheetTitle>Quote pricing rules</SheetTitle>
            <SheetDescription>Only for this quotation. Global defaults: {globals.defaultMarginPct}% target, {globals.minMarginPct}% minimum.</SheetDescription></SheetHeader>
          <div className="space-y-5">
            <p className="text-xs text-muted-foreground">Target Profit Margin % is set at the top of the builder: taxable value = input value × (1 + margin ÷ 100).</p>
            <div className="space-y-1.5"><Label>Minimum profit margin %</Label>
              <Input type="number" disabled={off} value={rules.minMarginPct} onChange={set("minMarginPct")} data-testid="quote-min-margin-input" />
              {bad(rules.minMarginPct) && <p className="text-xs text-rose-600" data-testid="quote-min-error">Enter a margin from 0 to 94.99%.</p>}
              <p className="text-xs text-muted-foreground">Leave blank to use the global minimum. The quote can't be saved below this margin (profit ÷ input value).</p></div>
            {!off && <Button variant="ghost" onClick={() => onChange({ ...rules, minMarginPct: globals.minMarginPct })} data-testid="quote-pricing-reset">Reset to global minimum</Button>}
            {!canEdit && <p className="text-xs text-muted-foreground">You need the "Manage pricing rules" permission to change these.</p>}
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
}
